import { PATH } from 'constants/path';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spin, message } from 'antd';
import type { UserPlatformStatus } from 'types/credentialing';
import { EUserPlatformStatus } from 'types/credentialing';
import { SvgCheck14 } from '../SvgIcons';
import s from './s.module.less';
import { HomeInfo } from 'types/provider';
import { getVideoSetting, updateVideoSetting } from 'api/videoSetting';

interface Props {
    userPlatformStatus: UserPlatformStatus;
    isGroup?: boolean;
    homeInfo: HomeInfo;
    // 开启后两个 step 的状态完全由 slaTier 档位推导（与提交成功页原型一致）：
    // PRIORITY = 已 listed，STANDARD = 未 listed；Klarity Meet 初始一律未设默认（FLASH 已在上层 return null）。
    // 不传时保持原有行为：读真实 onboarding / videoSetting 接口（CredentialingPage 用）。
    deriveStateFromTier?: boolean;
}

const SpeedUpModule = ({ userPlatformStatus, isGroup, homeInfo, deriveStateFromTier }: Props) => {
    const navigate = useNavigate();
    const [meetingLinkGenerationType, setMeetingLinkGenerationType] = React.useState<'KLARITY_MEETING' | 'CUSTOM'>();
    const [settingDefault, setSettingDefault] = React.useState(false);
    // tier 驱动模式下，仅记录本次会话内用户点击「Set Klarity Meet as default」成功后的状态
    const [justSetDefault, setJustSetDefault] = React.useState(false);

    // Fetch video settings（tier 驱动模式不依赖真实 videoSetting 展示，跳过拉取）
    React.useEffect(() => {
        if (deriveStateFromTier) return;
        const fetchVideoSetting = async () => {
            try {
                const result = await getVideoSetting();
                if (result?.data?.data?.meetingLinkGenerationType) {
                    setMeetingLinkGenerationType(result.data.data.meetingLinkGenerationType);
                }
            } catch (err) {
                console.error('Failed to fetch video settings:', err);
            }
        };
        fetchVideoSetting();
    }, []);

    const handleSetKlarityMeet = async () => {
        setSettingDefault(true);
        try {
            const result = await updateVideoSetting('KLARITY_MEETING');
            if (result?.data?.code === 200) {
                setMeetingLinkGenerationType('KLARITY_MEETING');
                setJustSetDefault(true);
                message.success('Klarity Meet is now your default.');
            } else {
                message.error(result?.data?.message || 'Failed to update. Please try again.');
            }
        } catch (err) {
            message.error('Failed to update. Please try again.');
        } finally {
            setSettingDefault(false);
        }
    };
    // tier 驱动：listed 由档位推导（PRIORITY 已 listed，STANDARD 未 listed）；Klarity Meet 初始一律未设默认，
    //           仅在用户本次点击成功后置为已设。默认（非 tier 驱动）：读真实 onboarding / videoSetting 状态。
    const hasListMarketplace = deriveStateFromTier
        ? userPlatformStatus === EUserPlatformStatus.PRIORITY
        : homeInfo && homeInfo.completedOnboarding;
    const hasSetDefault = deriveStateFromTier ? justSetDefault : meetingLinkGenerationType === 'KLARITY_MEETING';

    // tier 驱动模式不依赖 videoSetting，只等 homeInfo；否则维持原有等待逻辑
    if ((!deriveStateFromTier && !meetingLinkGenerationType) || !homeInfo) {
        return <Spin />;
    }

    if (userPlatformStatus === EUserPlatformStatus.FLASH) return null;
    if (hasListMarketplace && hasSetDefault) return null;

    let subtitle: string;
    if (hasListMarketplace) {
        subtitle = isGroup
            ? 'All of your providers need to set Klarity Meet as their default to reach the fastest queue.'
            : "You're already on the Klarity marketplace. Set Klarity Meet as your default in Calendar to reach the fastest credentialing queue and get credentialed sooner.";
    } else {
        subtitle = isGroup
            ? "Complete your own practice onboarding first. Once that's done, you can invite each provider to get listed on the marketplace individually."
            : 'Get listed on the Klarity marketplace, then set Klarity Meet as your default, to reach the fastest queue.';
    }

    const step1BtnText = isGroup ? 'Learn more about Klarity Marketing solutions' : 'Get listed on the marketplace';

    return (
        <div className={s.crossSell}>
            <div className={s.crossSellTitle}>Reach the fastest credentialing queue</div>
            <p className={s.crossSellDesc}>{subtitle}</p>
            <div className={s.crossSellStep}>
                {hasListMarketplace ? (
                    <span className={`${s.crossSellDot} ${s.done}`}>
                        <SvgCheck14 />
                    </span>
                ) : (
                    <span className={`${s.crossSellDot} ${s.active}`}>1</span>
                )}
                <div className={s.crossSellStepBody}>
                    <div className={s.crossSellStepTitle}>Get listed on the marketplace</div>
                    {hasListMarketplace ? (
                        <div className={s.crossSellStepDone}>You&apos;re listed on the Klarity marketplace.</div>
                    ) : (
                        <Button className={s.crossSellBtn} onClick={() => navigate(PATH.PPA_ONBOARDING_STEPS)}>
                            {step1BtnText}
                        </Button>
                    )}
                </div>
            </div>
            <div className={s.crossSellStep}>
                {hasSetDefault && (
                    <span className={`${s.crossSellDot} ${s.done}`}>
                        <SvgCheck14 />
                    </span>
                )}
                {!hasSetDefault && hasListMarketplace && <span className={`${s.crossSellDot} ${s.active}`}>2</span>}
                {!hasSetDefault && !hasListMarketplace && <span className={`${s.crossSellDot} ${s.disabled}`}>2</span>}
                <div className={s.crossSellStepBody}>
                    <div className={s.crossSellStepTitle}>Set Klarity Meet as default</div>
                    {hasSetDefault ? (
                        <div className={s.crossSellStepDone}>Klarity Meet is set as your default.</div>
                    ) : (
                        <>
                            {!hasListMarketplace && (
                                <div className={s.crossSellStepDisabledText}>
                                    Available once you&apos;re listed on the marketplace.
                                </div>
                            )}
                            <Button
                                className={s.crossSellBtn}
                                disabled={!hasListMarketplace}
                                loading={settingDefault}
                                onClick={handleSetKlarityMeet}
                            >
                                Set Klarity Meet as default
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SpeedUpModule;
